// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Alert, Button, Card, Divider, Form, Input, Modal, Segmented, Typography } from "antd";
import { ArrowLeftOutlined, KeyOutlined, LockOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Homeserver } from "../api";
import type { RegistrationConfig, Session } from "../types";
import { homeserverBase, setHomeserver, isPackagedApp, PUBLIC_SERVER } from "../server";
import { loginPasskey, passkeySupported } from "../passkey";

// inviteFromUrl pulls a registration invite token out of the landing/APK deep
// link (?reg=<token>), so a click-through pre-fills the field and opens Register.
function inviteFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get("reg")?.trim() ?? "";
  } catch {
    return "";
  }
}

const hs = new Homeserver();
const NATIVE = isPackagedApp(); // native OR bundled desktop: pick a server, no same-origin "this server"
const DEFAULT_OPTION = NATIVE ? "Zalmoxis" : "This server";

function modeFromBase(base: string): "This server" | "Zalmoxis" | "Self-hosted" {
  if (base === "") return "This server";
  if (base === PUBLIC_SERVER) return "Zalmoxis";
  return "Self-hosted";
}

export function Login({
  onSession,
  initialMode = "Log in",
  onBack,
}: {
  readonly onSession: (s: Session) => void;
  readonly initialMode?: "Log in" | "Register";
  readonly onBack?: () => void;
}) {
  const { message } = App.useApp();
  const urlInvite = inviteFromUrl();
  const [mode, setMode] = useState<"Log in" | "Register">(urlInvite ? "Register" : initialMode);
  const [loading, setLoading] = useState(false);
  const initialBase = homeserverBase();
  const [serverMode, setServerMode] = useState<string>(NATIVE && initialBase === "" ? DEFAULT_OPTION : modeFromBase(initialBase));
  const [selfUrl, setSelfUrl] = useState(initialBase && initialBase !== PUBLIC_SERVER ? initialBase : "");
  const [reg, setReg] = useState<RegistrationConfig | null>(null);
  const [invite, setInvite] = useState(urlInvite);

  // Load the selected server's PUBLIC registration policy so the form knows
  // whether sign-up is open and whether an invite is required.
  useEffect(() => {
    applyServer(serverMode, selfUrl);
    let live = true;
    hs.registrationConfig()
      .then((rc) => live && setReg(rc))
      .catch(() => live && setReg(null));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMode, selfUrl]);

  const regMode = reg?.mode ?? "public";
  const inviteRequired = mode === "Register" && regMode === "invite";
  const registrationClosed = mode === "Register" && regMode === "none";

  // applyServer persists the chosen homeserver so api calls target it.
  function applyServer(m: string, url: string) {
    if (m === "Zalmoxis") setHomeserver(PUBLIC_SERVER);
    else if (m === "This server") setHomeserver("");
    else setHomeserver(url);
  }

  async function submit(values: { username: string; password: string }) {
    applyServer(serverMode, selfUrl);
    if (serverMode === "Self-hosted" && !selfUrl.trim()) {
      message.error("Enter your self-hosted server URL");
      return;
    }
    if (inviteRequired && !invite.trim()) {
      message.error("This server is invite-only — paste your invite code");
      return;
    }
    setLoading(true);
    try {
      // Re-use the device already provisioned for this (server, account) so we don't pile up a new
      // device + key on every login; the id survives logout, so the next login reuses it.
      const devKey = `zalmoxis.device.${homeserverBase()}|${values.username}`;
      const storedDevice = mode === "Log in" ? (localStorage.getItem(devKey) ?? "") : "";
      const session =
        mode === "Register"
          ? await hs.register(values.username, values.password, "web", invite.trim())
          : await hs.login(values.username, values.password, "web", storedDevice);
      localStorage.setItem(devKey, session.device_id);
      // Drop the one-time ?reg= invite from the URL so a refresh/bookmark doesn't
      // re-trigger sign-up with a now-consumed token.
      if (urlInvite) globalThis.history.replaceState({}, "", globalThis.location.pathname);
      onSession(session);
    } catch (e) {
      message.error(`${mode} failed: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  // passkeyLogin runs a usernameless WebAuthn assertion and, on success, remembers
  // the resolved device so a later password login on this machine reuses it.
  async function passkeyLogin() {
    applyServer(serverMode, selfUrl);
    setLoading(true);
    try {
      const session = await loginPasskey("web", "");
      const lp = session.user_id.replace(/^@/, "").split(":")[0];
      localStorage.setItem(`zalmoxis.device.${homeserverBase()}|${lp}`, session.device_id);
      onSession(session);
    } catch (e) {
      message.error(`Passkey sign-in failed: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  // oidcLogin redirects the browser to the homeserver's OIDC start endpoint; the
  // provider returns to the SPA with a session in the URL fragment (handled in App).
  function oidcLogin(id: string) {
    applyServer(serverMode, selfUrl);
    globalThis.location.href = `${homeserverBase()}/client/v1/oidc/${encodeURIComponent(id)}/start`;
  }

  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverBusy, setRecoverBusy] = useState(false);

  async function recover(values: { username: string; code: string; password: string }) {
    applyServer(serverMode, selfUrl);
    setRecoverBusy(true);
    try {
      await hs.recoverPassword(values.username.trim(), values.code.trim(), values.password);
      setRecoverOpen(false);
      message.success("Password reset — sign in with your new password");
    } catch (e) {
      message.error(`Reset failed: ${(e as Error).message}`);
    } finally {
      setRecoverBusy(false);
    }
  }

  const showPasskey = mode === "Log in" && reg?.webauthn === true && passkeySupported();
  // OIDC works via a full-page redirect to the provider and back to the web app, so it
  // isn't offered in the native (Capacitor) shell — desktop / mobile-web only.
  const oidcProviders = NATIVE ? [] : (reg?.oidc_providers ?? []);
  const showAlt = showPasskey || oidcProviders.length > 0;

  return (
    <div className="login-screen">
      <Card className="login-card" variant="borderless">
        {onBack && (
          <Button type="link" className="login-back" icon={<ArrowLeftOutlined />} onClick={onBack}>
            Home
          </Button>
        )}
        <div className="brand">
          <img className="brand-wordmark" src="/logo-wordmark.svg" alt="Zalmoxis" />
        </div>
        <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
          Federated, end-to-end-encrypted, IRC-flavoured chat.
        </Typography.Paragraph>

        {urlInvite && mode === "Register" && (
          <Alert
            type="success"
            showIcon
            message="You've been invited 🎉"
            description="Create your account below — your invite is already filled in."
            style={{ marginBottom: 16 }}
          />
        )}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Server
        </Typography.Text>
        <Segmented
          block
          options={NATIVE ? ["Zalmoxis", "Self-hosted"] : ["This server", "Self-hosted"]}
          value={serverMode}
          onChange={(v) => {
            setServerMode(v as string);
            applyServer(v as string, selfUrl);
          }}
          style={{ marginTop: 4, marginBottom: 8 }}
        />
        {serverMode === "Self-hosted" ? (
          <Input
            placeholder="https://your-server.example"
            value={selfUrl}
            onChange={(e) => {
              setSelfUrl(e.target.value);
              applyServer("Self-hosted", e.target.value);
            }}
            autoCapitalize="none"
            size="large"
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
            {serverMode === "Zalmoxis" ? PUBLIC_SERVER.replace(/^https?:\/\//, "") : "the server hosting this app"}
          </Typography.Paragraph>
        )}

        <Segmented
          options={["Log in", "Register"]}
          value={mode}
          onChange={(v) => setMode(v as "Log in" | "Register")}
          block
          style={{ marginBottom: 16 }}
        />

        {registrationClosed ? (
          <Alert
            type="info"
            showIcon
            message="Registration is closed"
            description="This homeserver isn't accepting new sign-ups. Switch to Log in if you already have an account."
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Form layout="vertical" onFinish={submit} requiredMark={false}>
            <Form.Item name="username" rules={[{ required: true, message: "Pick a username" }]}>
              <Input prefix={<UserOutlined />} placeholder="username" autoComplete="username" size="large" />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, min: 8, message: "At least 8 characters" }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="password" autoComplete="current-password" size="large" />
            </Form.Item>
            {inviteRequired && (
              <Form.Item help="This server is invite-only — paste the code from your invite link." style={{ marginBottom: 16 }}>
                <Input
                  prefix={<MailOutlined />}
                  placeholder="invite code"
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                  autoCapitalize="none"
                  autoComplete="off"
                  size="large"
                />
              </Form.Item>
            )}
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              {mode}
            </Button>
          </Form>
        )}

        {showAlt && (
          <>
            <Divider plain style={{ margin: "16px 0", fontSize: 12 }}>
              or
            </Divider>
            {showPasskey && (
              <Button icon={<KeyOutlined />} block size="large" onClick={passkeyLogin} loading={loading} className="passkey-login">
                Sign in with a passkey
              </Button>
            )}
            {oidcProviders.map((p) => (
              <Button key={p.id} block size="large" onClick={() => oidcLogin(p.id)} className="oidc-login" style={{ marginTop: 8 }}>
                Sign in with {p.name}
              </Button>
            ))}
          </>
        )}

        {mode === "Log in" && !registrationClosed && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Button type="link" size="small" className="forgot-password" onClick={() => setRecoverOpen(true)}>
              Forgot your password?
            </Button>
          </div>
        )}
      </Card>

      <Modal open={recoverOpen} title="Reset your password" footer={null} onCancel={() => setRecoverOpen(false)}>
        <Typography.Paragraph type="secondary">
          Enter your username and one of your one-time recovery codes to set a new password. This signs you
          out everywhere. (Recovery codes are set up in Profile → Security while you're signed in.)
        </Typography.Paragraph>
        <Form layout="vertical" onFinish={recover} requiredMark={false}>
          <Form.Item name="username" rules={[{ required: true, message: "Your username" }]}>
            <Input prefix={<UserOutlined />} placeholder="username" autoCapitalize="none" autoComplete="username" />
          </Form.Item>
          <Form.Item name="code" rules={[{ required: true, message: "A recovery code" }]}>
            <Input prefix={<MailOutlined />} placeholder="recovery code (e.g. k7npq-r3wxy)" autoCapitalize="none" autoComplete="off" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, min: 8, message: "At least 8 characters" }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="new password" autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={recoverBusy}>
            Reset password
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
